import { useEffect, useRef, type RefObject } from 'react';
import { isEditableTarget } from '../utils/isEditableTarget';

type IframeSource = HTMLIFrameElement | RefObject<HTMLIFrameElement | null> | null | undefined;

function asIframe(source: IframeSource): HTMLIFrameElement | null {
  if (!source) return null;
  if (source instanceof HTMLIFrameElement) return source;
  return source.current;
}

export function isCompareSyncHotkey(e: Pick<KeyboardEvent, 'code' | 'key'>): boolean {
  return e.code === 'KeyS' || e.key.toLowerCase() === 's';
}

function bindKeydown(target: Window | null | undefined, handler: (e: KeyboardEvent) => void): (() => void) | null {
  if (!target) return null;
  try {
    target.addEventListener('keydown', handler, true);
    return () => target.removeEventListener('keydown', handler, true);
  } catch {
    return null;
  }
}

/**
 * S toggles compare/scroll/camera sync while a split view is open.
 * Skips fields and modifier chords (Cmd/Ctrl+S stays Save).
 * Same-origin iframes are included so the key still works after clicking a pane.
 */
export function useCompareSyncHotkey(
  enabled: boolean,
  onToggle: () => void,
  leftIframe?: IframeSource,
  rightIframe?: IframeSource,
  bindKey = '',
): void {
  const onToggleRef = useRef(onToggle);
  onToggleRef.current = onToggle;

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey || e.repeat) return;
      if (isEditableTarget(e.target)) return;
      if (!isCompareSyncHotkey(e)) return;
      e.preventDefault();
      e.stopPropagation();
      onToggleRef.current();
    };

    const cleanups: Array<() => void> = [];
    const bound = new Set<Window>();
    const bind = (target: Window | null | undefined) => {
      if (!target || bound.has(target)) return;
      const unbind = bindKeydown(target, onKeyDown);
      if (!unbind) return;
      bound.add(target);
      cleanups.push(unbind);
    };

    bind(window);
    for (const frame of [asIframe(leftIframe), asIframe(rightIframe)]) {
      bind(frame?.contentWindow ?? undefined);
      if (!frame) continue;
      const onLoad = () => bind(frame.contentWindow);
      frame.addEventListener('load', onLoad);
      cleanups.push(() => frame.removeEventListener('load', onLoad));
    }

    return () => {
      cleanups.forEach(fn => fn());
    };
  }, [enabled, leftIframe, rightIframe, bindKey]);
}
